













































































import { Component, Prop, Vue } from "vue-property-decorator";

const escapeHtml = (s: string): string =>
    s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

/** Inline markdown: `code`, **bold**, *italic*. */
const renderInline = (text: string): string =>
    escapeHtml(text)
        .replace(/`([^`]+)`/g, "<code>$1</code>")
        .replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")
        .replace(/\*([^*]+)\*/g, "<em>$1</em>");

const isGeneratedNotice = (line: string): boolean =>
    /^\s*_?Generated from .*?_?\s*$/i.test(line);

/** Drop a trailing "[Badge]" suffix from a line. */
const stripTrailingBadges = (text: string): string => {
    const m = text.match(/^(.*?)\s*\[([^\]]+)\]\s*$/);
    return m ? m[1] : text;
};

/** Render a metric's body lines (paragraphs + bullet lists; no headings) to HTML. */
const renderMarkdownBody = (lines: string[]): string => {
    const out: string[] = [];
    let inList = false;
    const closeList = () => {
        if (inList) {
            out.push("</ul>");
            inList = false;
        }
    };
    for (const raw of lines) {
        const line = raw.trimEnd();
        if (!line.trim()) {
            closeList();
            continue;
        }
        if (isGeneratedNotice(line)) continue;
        if (/^Badges:\s*/.test(line)) {
            closeList();
            continue;
        }
        if (/^[-*]\s+/.test(line)) {
            if (!inList) {
                out.push("<ul>");
                inList = true;
            }
            const item = stripTrailingBadges(line.replace(/^[-*]\s+/, ""));
            out.push(`<li>${renderInline(item)}</li>`);
            continue;
        }
        closeList();
        out.push(`<p>${renderInline(stripTrailingBadges(line))}</p>`);
    }
    closeList();
    return out.join("\n");
};

@Component
export default class DefinitionsTab extends Vue {
    /** Markdown sourced from the metric catalog endpoint, fetched once at the dashboard root. */
    @Prop({ default: "" }) definitionsMarkdown!: string;

    /** Free-text filter — case-insensitive substring match on each metric's name + body. */
    query: string = "";

    /** Slugs of metrics currently expanded (accordion: usually a single entry). */
    openSlugs: string[] = [];

    /**
     * Parse the catalog markdown once into metric blocks (`### ` heading + body lines), keep the
     * ones matching the query, and render each body to HTML — alphabetical by label. The slimmed
     * web catalog is a flat list of `### ` metrics under the page title, so any other heading
     * (the title) just ends the current block.
     */
    get metricSections(): { label: string; slug: string; bodyHtml: string }[] {
        const q = this.query.trim().toLowerCase();
        const blocks: { label: string; body: string[] }[] = [];
        let current: { label: string; body: string[] } | null = null;
        for (const raw of this.definitionsMarkdown.split(/\r?\n/)) {
            const h3 = raw.match(/^###\s+(.*)$/);
            if (h3) {
                current = { label: h3[1].trim(), body: [] };
                blocks.push(current);
            } else if (/^#{1,6}\s+/.test(raw)) {
                current = null;
            } else if (current) {
                current.body.push(raw);
            }
        }
        const used: Record<string, number> = {};
        return blocks
            .filter(
                (b) =>
                    !q ||
                    `${b.label}\n${b.body.join("\n")}`
                        .toLowerCase()
                        .includes(q),
            )
            .map((b) => {
                let slug = this.slugify(b.label);
                if (used[slug] != null) slug = `${slug}-${(used[slug] += 1)}`;
                else used[slug] = 0;
                return {
                    label: b.label,
                    slug,
                    bodyHtml: this.highlightQuery(renderMarkdownBody(b.body)),
                };
            })
            .sort((a, b) =>
                a.label.localeCompare(b.label, undefined, {
                    sensitivity: "base",
                }),
            );
    }

    /** Slugify a metric label into a stable key. */
    private slugify(text: string): string {
        return (
            text
                .toLowerCase()
                .replace(/\*/g, "")
                .replace(/[^a-z0-9]+/g, "-")
                .replace(/^-+|-+$/g, "") || "metric"
        );
    }

    isOpen(slug: string): boolean {
        return this.openSlugs.includes(slug);
    }

    /** Accordion: open a closed metric on its own; clicking an open one just closes it. */
    togglePanel(slug: string): void {
        this.openSlugs = this.isOpen(slug)
            ? this.openSlugs.filter((s) => s !== slug)
            : [slug];
    }

    expandAll(): void {
        this.openSlugs = this.metricSections.map((m) => m.slug);
    }

    collapseAll(): void {
        this.openSlugs = [];
    }

    /** True when every metric section is expanded — drives the single toggle button. */
    get allExpanded(): boolean {
        return (
            this.metricSections.length > 0 &&
            this.openSlugs.length === this.metricSections.length
        );
    }

    /** One button: collapse everything if all are open, otherwise expand everything. */
    toggleAll(): void {
        if (this.allExpanded) this.collapseAll();
        else this.expandAll();
    }

    /** Wrap query matches in <mark> (the yellow highlight). */
    private highlightQuery(html: string): string {
        const q = this.query.trim();
        if (!q) return html;
        const re = new RegExp(q.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "gi");
        return html.replace(/(<[^>]+>)|([^<]+)/g, (_m, tag, text) =>
            tag ? tag : text.replace(re, "<mark>$&</mark>"),
        );
    }
}
